import { useState, useEffect } from 'react'
import { useParams, Link, useLocation } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { 
  ArrowLeft, 
  Server, 
  Activity, 
  Settings, 
  Trash2,
  Play,
  Square,
  RefreshCw,
  Shield,
  Database,
  Zap,
  BarChart3,
  Globe,
  Clock,
  Wifi
} from 'lucide-react'
import { toast } from 'sonner'
import { ServerMonitor } from '@/components/monitoring/ServerMonitor'
import { ActivityLog } from '@/components/monitoring/ActivityLog'
// import { MetricsChart } from '@/components/monitoring/MetricsChart'
// import { AlertSystem } from '@/components/monitoring/AlertSystem'
import { AuthenticationTab } from '@/components/auth/AuthenticationTab'
import { OpenAIMCPConfig } from '@/components/auth/OpenAIMCPConfig'
import { ToolsTab } from '@/components/servers/ToolsTab'
import { ResourcesTab } from '@/components/servers/ResourcesTab'
import { PromptsTab } from '@/components/servers/PromptsTab'
import { CacheTab } from '@/components/servers/CacheTab'
import { ErrorBoundary } from '@/components/ui/error-boundary'
import { api, serversApi } from '@/lib/api'
import { useQuery } from '@tanstack/react-query'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { useQueryClient } from '@tanstack/react-query'
import { LogsTab } from '@/components/servers/LogsTab'

interface ServerFeature {
  name: string
  icon: React.ComponentType<{ className?: string }>
  variant: 'default' | 'destructive' | 'outline' | 'secondary'
  description: string
}

interface ServerDetail {
  id: string
  name: string
  // port: number
  transport: string
  status: string
  tools_count?: number
  auth_supported?: boolean
  cache_enabled?: boolean
  created_at?: string
  openapi_spec?: any
  config?: any
}

interface ServerConfig {
  id: string
  name: string
  version: string
  transport: string
  status: string
  created_at: string
  updated_at: string
  openapi_spec: any
  cache?: {
    enabled: boolean
    max_size?: number
    default_ttl?: number
  }
}

interface ToolsResponse {
  server_id: string
  tools: any[]
  count: number
}

export function ServerDetailPage() {
  const { id } = useParams<{ id: string }>()
  const location = useLocation()
  const [server, setServer] = useState<ServerDetail | null>(null)
  const [loading, setLoading] = useState(true)
  
  const [actionLoading, setActionLoading] = useState<string>('')

  const { data: config, isLoading: configLoading, error: configError } = useQuery<ServerConfig>({
    queryKey: ['serverConfig', id],
    queryFn: async () => await api.get(`/servers/${id}/config`),
    enabled: !!id,
  })

  // Fetch tools count separately
  const { data: toolsData, isLoading: toolsLoading } = useQuery<ToolsResponse>({
    queryKey: ['serverTools', id],
    queryFn: async () => await api.get(`/servers/${id}/tools`),
    enabled: !!id,
    retry: false, // Don't retry if tools endpoint fails
  })

  const queryClient = useQueryClient()

  const [isEditing, setIsEditing] = useState(false)
  const [editedName, setEditedName] = useState('')
  const [editedTransport, setEditedTransport] = useState('')
  const [editedSpec, setEditedSpec] = useState('')

  // Fetch server details when the id changes
  useEffect(() => {
    if (id) {
      fetchServerDetails()
    }
  }, [id])

  // Update local edit fields when config changes
  useEffect(() => {
    if (config) {
      setEditedName(config.name)
      setEditedTransport(config.transport)
      setEditedSpec(JSON.stringify(config.openapi_spec, null, 2))
    }
  }, [config])

  const fetchServerDetails = async () => {
    if (!id) return
    
    try {
      setLoading(true)
      const response = await api.get(`/servers/${id}`)
      console.log('ServerDetailPage: Got server response:', response)
      setServer(response as any)
    } catch (error) {
      console.error('Failed to fetch server details:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleServerAction = async (action: string) => {
    if (!server) return
    
    try {
      setActionLoading(action)
      if (action === 'restart') {
        await api.post(`/servers/${server.id}/restart`)
      } else if (action === 'delete') {
        await serversApi.delete(server.id)
        // Redirect to servers list after successful deletion
        window.location.href = '/servers'
        return
      } else if (action === 'start') {
        await api.post(`/servers/${server.id}/start`)
      } else if (action === 'stop') {
        await api.post(`/servers/${server.id}/stop`)
      } else {
        await api.post(`/servers/${server.id}/${action}`)
      }
      
      // Refresh server details after action
      fetchServerDetails()
    } catch (error) {
      console.error(`Failed to ${action} server:`, error)
    } finally {
      setActionLoading('')
    }
  }

  const handleSave = async () => {
    try {
      let parsedSpec
      try {
        parsedSpec = JSON.parse(editedSpec)
      } catch (e) {
        toast.error('Invalid JSON in OpenAPI specification')
        return
      }

      const req = {
        server_name: editedName,
        transport: editedTransport,
        openapi_spec: parsedSpec
      }

      await api.put(`/servers/${id}`, req)
      setIsEditing(false)
      queryClient.invalidateQueries({ queryKey: ['serverConfig', id] })
      fetchServerDetails()
      toast.success('Server configuration updated successfully')
    } catch (err) {
      toast.error('Failed to update server')
    }
  }

  const getStatusColor = (status?: string) => {
    const normalizedStatus = (status ?? '').toLowerCase()
    switch (normalizedStatus) {
      case 'running': return 'bg-green-500'
      case 'stopped': return 'bg-red-500'
      case 'starting': return 'bg-yellow-500'
      default: return 'bg-gray-500'
    }
  }

  // Enhanced feature detection
  const detectServerFeatures = (): ServerFeature[] => {
    const features: ServerFeature[] = []
    
    if (!config) return features

    // Authentication detection
    const hasAuth = config.openapi_spec?.components?.security_schemes && 
                   Object.keys(config.openapi_spec.components.security_schemes).length > 0
    if (hasAuth) {
      features.push({
        name: 'Auth',
        icon: Shield,
        variant: 'default' as const,
        description: 'Authentication required'
      })
    }

    // Cache detection
    if (config.cache?.enabled) {
      features.push({
        name: 'Cache',
        icon: Database,
        variant: 'default' as const,
        description: 'Response caching enabled'
      })
    }

    // Transport-specific features
    switch (config.transport?.toLowerCase()) {
      case 'websocket':
        features.push({
          name: 'WebSocket',
          icon: Wifi,
          variant: 'secondary' as const,
          description: 'Real-time bidirectional communication'
        })
        break
      case 'sse':
        features.push({
          name: 'SSE',
          icon: Zap,
          variant: 'secondary' as const,
          description: 'Server-Sent Events streaming'
        })
        break
      case 'http':
        features.push({
          name: 'HTTP',
          icon: Globe,
          variant: 'outline' as const,
          description: 'Standard HTTP JSON-RPC'
        })
        break
      case 'stdio':
        features.push({
          name: 'Stdio',
          icon: Activity,
          variant: 'outline' as const,
          description: 'Direct process communication'
        })
        break
    }

    // Monitoring (always available)
    features.push({
      name: 'Monitoring',
      icon: BarChart3,
      variant: 'outline' as const,
      description: 'Real-time performance monitoring'
    })

    // Analytics (always available) 
    features.push({
      name: 'Analytics',
      icon: Activity,
      variant: 'outline' as const,
      description: 'Usage analytics and metrics'
    })

    return features
  }

  // Get actual tools count
  const getToolsCount = () => {
    if (toolsData?.count !== undefined) {
      return toolsData.count
    }
    
    // Fallback: count endpoints from config
    if (config?.openapi_spec?.endpoints) {
      return config.openapi_spec.endpoints.length
    }
    
    return 0
  }

  // Get transport description
  const getTransportDescription = (transport?: string) => {
    const transportInfo: Record<string, string> = {
      'http': 'Standard HTTP JSON-RPC',
      'websocket': 'Real-time bidirectional communication', 
      'sse': 'Server-Sent Events streaming',
      'stdio': 'Direct process communication'
    }
    const key = (transport ?? '').toLowerCase()
    return transportInfo[key] || 'Unknown transport'
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <div className="h-8 w-8 bg-gray-200 rounded animate-pulse"></div>
          <div className="h-8 bg-gray-200 rounded w-48 animate-pulse"></div>
        </div>
        <div className="grid gap-6 md:grid-cols-2">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader>
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </CardHeader>
              <CardContent>
                <div className="h-20 bg-gray-200 rounded"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    )
  }

  if (!server) {
    return (
      <div className="text-center py-12">
        <Server className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-semibold">Server not found</h3>
        <p className="text-muted-foreground mb-4">
          The server you're looking for doesn't exist or has been deleted.
        </p>
        <Button asChild>
          <Link to="/servers">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Servers
          </Link>
        </Button>
      </div>
    )
  }

  const serverFeatures = detectServerFeatures()
  const toolsCount = getToolsCount()

  // Support deep-linking to tabs via ?tab=...
  const searchParams = new URLSearchParams(location.search)
  const initialTab = (searchParams.get('tab') || 'overview') as string

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/servers">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{server.name}</h1>
            <p className="text-muted-foreground font-mono text-sm">{server.id}</p>
          </div>
          <div className="flex items-center space-x-2">
            <div 
              className={`w-3 h-3 rounded-full ${getStatusColor(server.status)}`}
              title={server.status}
            />
            <Badge variant="outline">{server.status}</Badge>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          {server.status === 'running' ? (
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => handleServerAction('stop')}
              disabled={!!actionLoading}
            >
              <Square className="mr-2 h-4 w-4" />
              Stop
            </Button>
          ) : (
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => handleServerAction('start')}
              disabled={!!actionLoading}
            >
              <Play className="mr-2 h-4 w-4" />
              Start
            </Button>
          )}
          
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => handleServerAction('restart')}
            disabled={!!actionLoading}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Restart
          </Button>
          
          <Button 
            variant="destructive" 
            size="sm"
            onClick={() => handleServerAction('delete')}
            disabled={!!actionLoading}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete
          </Button>
        </div>
      </div>

      {/* Content */}
      <Tabs defaultValue={initialTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="monitoring">Monitoring</TabsTrigger>
          <TabsTrigger value="tools">Tools</TabsTrigger>
          <TabsTrigger value="resources">Resources</TabsTrigger>
          <TabsTrigger value="prompts">Prompts</TabsTrigger>
          <TabsTrigger value="auth">Authentication</TabsTrigger>
          <TabsTrigger value="cache">Cache</TabsTrigger>
          <TabsTrigger value="logs">Logs</TabsTrigger>
          <TabsTrigger value="config">Configuration</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Transport</CardTitle>
                <Server className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold capitalize">{server.transport}</div>
                {/* <p className="text-xs text-muted-foreground">
                  Port {server.port}
                </p> */}
                <p className="text-xs text-muted-foreground mt-1">
                  {getTransportDescription(server.transport)}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Tools</CardTitle>
                <Settings className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {toolsLoading ? (
                    <span className="animate-pulse">...</span>
                  ) : (
                    toolsCount
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  {toolsLoading ? 'Loading...' : 'Available tools'}
                </p>
                {toolsData && (
                  <p className="text-xs text-green-600 mt-1">
                    ✓ Live count from API
                  </p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Status</CardTitle>
                <Activity className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold capitalize">{server.status}</div>
                <p className="text-xs text-muted-foreground">
                  Current state
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Features</CardTitle>
                <Settings className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-1">
                  {serverFeatures.length > 0 ? (
                    serverFeatures.slice(0, 3).map((feature, index) => (
                      <Badge 
                        key={index}
                        variant={feature.variant} 
                        className="text-xs"
                        title={feature.description}
                      >
                        <feature.icon className="w-3 h-3 mr-1" />
                        {feature.name}
                      </Badge>
                    ))
                  ) : (
                    <Badge variant="outline" className="text-xs">
                      <Activity className="w-3 h-3 mr-1" />
                      Basic
                    </Badge>
                  )}
                  {serverFeatures.length > 3 && (
                    <Badge variant="outline" className="text-xs">
                      +{serverFeatures.length - 3}
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {serverFeatures.length} features enabled
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Enhanced Features Overview */}
          {serverFeatures.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Feature Overview</CardTitle>
                <CardDescription>
                  Detailed breakdown of server capabilities and features
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {serverFeatures.map((feature, index) => (
                    <div key={index} className="flex items-start space-x-3 p-3 rounded-lg border">
                      <feature.icon className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      <div>
                        <h4 className="font-medium text-sm">{feature.name}</h4>
                        <p className="text-xs text-muted-foreground">{feature.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Quick Status Overview */}
          <div className="mt-6">
            <h3 className="text-lg font-semibold mb-4">Server Status</h3>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Health</CardTitle>
                  <Activity className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-green-600">Healthy</div>
                  <p className="text-xs text-muted-foreground">
                    Last checked: Just now
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Uptime</CardTitle>
                  <Clock className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">2h 15m</div>
                  <p className="text-xs text-muted-foreground">
                    Since last restart
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Requests</CardTitle>
                  <Activity className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">1,234</div>
                  <p className="text-xs text-muted-foreground">
                    Total processed
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Response Time</CardTitle>
                  <Activity className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">245ms</div>
                  <p className="text-xs text-muted-foreground">
                    Average response
                  </p>
                </CardContent>
              </Card>
            </div>
            
            <div className="mt-4">
              <p className="text-sm text-muted-foreground">
                💡 For detailed real-time monitoring, visit the <strong>Monitoring</strong> tab.
              </p>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="monitoring" className="space-y-6">
          <div className="grid gap-6">
            {/* Single Real-time Server Monitor - this will coordinate all monitoring data */}
            <ServerMonitor serverId={server.id} />
          
            {/* Activity Log with reduced polling interval */}
            <ActivityLog serverId={server.id} />
            
            {/* Note about monitoring optimization */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  ℹ️ Monitoring Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Monitoring data refreshes every 10 seconds to provide real-time insights while optimizing performance.
                  All metrics are automatically updated to prevent excessive API calls.
                </p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="tools">
          <ErrorBoundary>
            <ToolsTab 
              serverId={server?.id || ''} 
              serverName={server?.name || ''} 
            />
          </ErrorBoundary>
        </TabsContent>

        <TabsContent value="logs">
          <ErrorBoundary>
            <LogsTab 
              serverId={server?.id || ''}
              serverName={server?.name || ''}
            />
          </ErrorBoundary>
        </TabsContent>

        <TabsContent value="resources">
          <ErrorBoundary>
            <ResourcesTab 
              serverId={server?.id || ''}
              serverName={server?.name || ''}
            />
          </ErrorBoundary>
        </TabsContent>

        <TabsContent value="prompts">
          <ErrorBoundary>
            <PromptsTab 
              serverId={server?.id || ''}
              serverName={server?.name || ''}
            />
          </ErrorBoundary>
        </TabsContent>

        <TabsContent value="auth" className="space-y-6">
          <AuthenticationTab 
            serverId={server?.id || ''}
            serverName={server?.name || ''}
            transport={server?.transport || ''}
            // port={server?.port || 0}
            port={0} // Placeholder for port, adjust as needed
          />
          
          <OpenAIMCPConfig 
            serverId={server?.id || ''}
            serverName={server?.name || ''}
          />
        </TabsContent>

        <TabsContent value="cache">
          <ErrorBoundary>
            <CacheTab 
              serverId={server?.id || ''} 
              serverName={server?.name || ''} 
            />
          </ErrorBoundary>
        </TabsContent>

        <TabsContent value="config">
          <Card>
            <CardHeader>
              <CardTitle>Configuration</CardTitle>
              <CardDescription>
                Server configuration and OpenAPI specification
              </CardDescription>
            </CardHeader>
            <CardContent>
              {configLoading ? (
                <p className="text-muted-foreground">Loading configuration...</p>
              ) : configError ? (
                <p className="text-red-500">Error loading configuration: {configError.message}</p>
              ) : config ? (
                <>
                  <div className="flex justify-end mb-4">
                    {isEditing ? (
                      <>
                        <Button variant="outline" onClick={() => setIsEditing(false)} className="mr-2">
                          Cancel
                        </Button>
                        <Button onClick={handleSave}>
                          Save
                        </Button>
                      </>
                    ) : (
                      <Button onClick={() => setIsEditing(true)}>
                        Edit
                      </Button>
                    )}
                  </div>
                  {isEditing ? (
                    <div className="space-y-6">
                      <div>
                        <h3 className="text-lg font-semibold mb-2">Server Configuration</h3>
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium mb-1">Name</label>
                            <Input value={editedName} onChange={(e) => setEditedName(e.target.value)} />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-1">Transport</label>
                            <Select value={editedTransport} onValueChange={setEditedTransport}>
                              <SelectTrigger>
                                <SelectValue placeholder="Select transport" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="shttp">Streamable HTTP</SelectItem>
                                <SelectItem value="http">HTTP</SelectItem>
                                <SelectItem value="websocket">WebSocket</SelectItem>
                                <SelectItem value="sse">SSE</SelectItem>
                                <SelectItem value="stdio">Stdio</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold mb-2">OpenAPI Specification</h3>
                        <Textarea 
                          value={editedSpec} 
                          onChange={(e) => setEditedSpec(e.target.value)} 
                          className="font-mono text-sm min-h-[400px]"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      <div>
                        <h3 className="text-lg font-semibold mb-2">Server Configuration</h3>
                        <pre className="bg-muted p-4 rounded-md overflow-auto text-sm">
                          <code>{JSON.stringify({
                            id: config.id,
                            name: config.name,
                            version: config.version,
                            transport: config.transport,
                            status: config.status,
                            created_at: config.created_at,
                            updated_at: config.updated_at
                          }, null, 2)}</code>
                        </pre>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold mb-2">OpenAPI Specification</h3>
                        <pre className="bg-muted p-4 rounded-md overflow-auto text-sm max-h-[500px]">
                          <code>{JSON.stringify(config.openapi_spec, null, 2)}</code>
                        </pre>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-muted-foreground">No configuration available</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
} 